/**
 * Session store (zustand). Holds identity, token, and the active room context.
 *
 * The token is persisted in sessionStorage (not localStorage) so it is scoped
 * to the tab and cleared when the tab closes; it is wiped on 401. Room/role/
 * mediaDetail are transient — they are re-acquired by joining the room.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  MediaDetail,
  Role,
  RoomSummary,
  SessionUser,
} from '@syncreel/shared';
import { postSession, revokeSession } from '../api/client';
import { disconnectSocket } from '../socket/socket';
import { logger } from '../api/logger';

/** Shape of the persisted + ephemeral session state. */
export interface SessionState {
  user: SessionUser | null;
  token: string | null;
  /** Admin token (X-Admin-Token) entered on the /admin page; persisted so the
   * admin stays verified across reloads in the same tab. */
  adminToken: string | null;
  /** Active room summary (set on create/join). */
  room: RoomSummary | null;
  /** Caller's role in the active room. */
  role: Role | null;
  /** Media detail for the active room's media. */
  mediaDetail: MediaDetail | null;
  /** Join code, known only to the host at creation time. */
  joinCode: string | null;
  /** Host key, known only to the host at creation time; used for host promotion. */
  hostKey: string | null;
  /** Create an anonymous session (POST /api/auth/session). */
  createSession: (displayName: string) => Promise<void>;
  /** Set the active room context after create/join. `mediaDetail` may be null
   * after creation (the create response omits it); RoomPage re-joins to fetch
   * the full detail and mint the media cookie. */
  setRoom: (ctx: {
    room: RoomSummary;
    role: Role;
    mediaDetail: MediaDetail | null;
    joinCode: string | null;
    hostKey: string | null;
  }) => void;
  /** Store the admin token after it has been verified on /admin. */
  setAdminToken: (token: string) => void;
  /** Clear the stored admin token (keep identity + room). */
  clearAdminToken: () => void;
  /** Clear only the active room context (keep identity). */
  clearRoom: () => void;
  /** Clear everything (identity + room); used on 401 and logout. */
  clearSession: () => void;
  /** Revoke the current session token server-side, then clear local state. */
  logout: () => Promise<void>;
}

/** State slice persisted to sessionStorage: identity + admin token. */
interface PersistedSlice {
  user: SessionUser | null;
  token: string | null;
  adminToken: string | null;
}

/**
 * Create the session store. Token + user persist to sessionStorage; room
 * context is in-memory only.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      adminToken: null,
      room: null,
      role: null,
      mediaDetail: null,
      joinCode: null,
      hostKey: null,

      createSession: async (displayName: string) => {
        const res = await postSession({ displayName });
        set({ user: res.user, token: res.token });
      },

      setRoom: ({ room, role, mediaDetail, joinCode, hostKey }) => {
        set({ room, role, mediaDetail, joinCode, hostKey });
      },

      setAdminToken: (token: string) => {
        set({ adminToken: token });
      },

      clearAdminToken: () => {
        set({ adminToken: null });
      },

      clearRoom: () => {
        disconnectSocket();
        set({ room: null, role: null, mediaDetail: null, joinCode: null, hostKey: null });
      },

      clearSession: () => {
        disconnectSocket();
        set({ user: null, token: null, adminToken: null, room: null, role: null, mediaDetail: null, joinCode: null, hostKey: null });
      },

      logout: async () => {
        // WHY: revoke the token server-side so it can't be reused even before
        // its natural expiry. Best-effort — if the network call fails, still
        // clear local state so the user is logged out locally.
        try {
          await revokeSession();
        } catch (err) {
          logger.warn('failed to revoke session server-side', err);
        }
        disconnectSocket();
        set({ user: null, token: null, adminToken: null, room: null, role: null, mediaDetail: null, joinCode: null, hostKey: null });
      },
    }),
    {
      name: 'syncreel.session',
      storage: createJSONStorage(() => sessionStorage),
      // WHY: persist only identity; room context is re-derived on join and
      // must never be trusted from storage (server is authoritative).
      partialize: (state): PersistedSlice => ({ user: state.user, token: state.token, adminToken: state.adminToken }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          logger.debug('session rehydrated', { userId: state.user?.userId });
        }
      },
    },
  ),
);
